import React from 'react';
import { CreditCardOutlined } from '@mui/icons-material';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { useQuery } from '@tanstack/react-query';
import { getCredits, getSubscriptionInformation } from '@/api/user';
import { useDispatch } from 'react-redux';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';

const SiderCreditsStatus: React.FC<{ forReseller?: boolean }> = ({ forReseller = false }) => {
	const dispatch = useDispatch();
	// Only show for non-reseller

	const loggedInUser = useAppSelector(getLoggedInUser);

	// Fetch credits
	const { data: creditDetails, isLoading: loadingCredits } = useQuery({
		queryKey: ['getUserCredits'],
		queryFn: () => getCredits('Client'),
		enabled: loggedInUser?.userType === 'Client',
	});

	// Fetch plan info
	const { data: subscriptionData, isLoading: loadingPlan } = useQuery({
		queryKey: ['subscription-information'],
		queryFn: () => getSubscriptionInformation('Client'),
		enabled: loggedInUser?.userType === 'Client',
	});

	if (
		loadingCredits ||
		loadingPlan ||
		!creditDetails?.data ||
		!subscriptionData?.data ||
		loggedInUser?.userType === 'SubAccount'
	) {
		return null;
	}

	// Calculate credits
	const voiceCredits = parseFloat(
		((creditDetails.data.voiceFreeCredits || 0) + (creditDetails.data.voiceCredits || 0)).toFixed(
			2,
		),
	);
	const smsCredits = parseFloat(
		((creditDetails.data.smsFreeCredits || 0) + (creditDetails.data.smsCredits || 0)).toFixed(2),
	);

	// Plan type
	const planType = subscriptionData.data.inboundPlan || 'Unknown';
	const planLabel =
		planType.toLowerCase() === 'pro'
			? 'Pro Plan'
			: planType.toLowerCase() === 'basic'
				? 'Basic Plan'
				: planType;

	if (forReseller) {
		return null;
	}

	return (
		<div
			className="credits-menu-item inline-flex mx-auto cursor-pointer rounded-lg w-full items-center p-2 px-4 whitespace-nowrap transition-all duration-200 ease-in-out bg-gradient-to-br from-slate-50 to-slate-200 hover:from-slate-100 hover:to-slate-300 border border-slate-300 min-h-[48px]"
			onClick={() => dispatch(setCurrentPage(ReceptionistDashboardSiderItem.CREDITS))}
		>
			<div className="flex items-center gap-3">
				<CreditCardOutlined className="text-slate-500 text-base" />
				<div className="flex flex-col items-start">
					<span className="text-sm font-medium text-slate-700">Credits</span>
					<span className="text-xs text-slate-500 font-normal mt-0.5">{planLabel}</span>
				</div>
			</div>

			<div className="flex flex-col gap-0.5 items-end ml-auto">
				<div className="flex items-center gap-1.5">
					<span className="text-xs text-slate-500 font-medium">📞</span>
					<span className="text-sm font-semibold text-slate-800 bg-blue-50 px-1.5 py-0.5 rounded-lg min-w-[28px] text-center">
						{voiceCredits.toFixed(1)}
					</span>
				</div>

				<div className="flex items-center gap-1.5">
					<span className="text-xs text-slate-500 font-medium">💬</span>
					<span className="text-sm font-semibold text-slate-800 bg-green-50 px-1.5 py-0.5 rounded-lg min-w-[28px] text-center">
						{smsCredits.toFixed(1)}
					</span>
				</div>
			</div>
		</div>
	);
};

export default SiderCreditsStatus;
